import type { BillingPlan } from "@app/common/types";
import Config from "@app/config";

interface PlanDetailConfig {
  description: string;
  isMostPopular: boolean;
  featureDescription: string;
  alternativeFeatureDescription?: string;
  features: { title: string; description?: string }[];
}

export const OPTS_BY_PLAN: Record<
  Exclude<BillingPlan, "unknown">,
  PlanDetailConfig
> = {
  free: {
    description: `Basic meeting measurement, summaries, and reporting for better meetings. Limited to ${Config.billing.monthlyRecordingQuota.free} meetings per month.`,
    isMostPopular: false,
    featureDescription: "Key features include:",
    features: [
      {
        title: `${Config.billing.monthlyRecordingQuota.free} meeting reports per month`,
        description:
          "Summary, transcription with audience reactions, action items, key questions, and topics.",
      },
      {
        title: "Video conferencing",
        description:
          "Support for Zoom, Google Meet, Microsoft Teams, and Webex",
      },
      {
        title: "Basic reporting",
        description: "Access to recommendations and coaching",
      },
      {
        title: "Smart Scheduler",
        description: "Get the free Calendar add-on",
      },
    ],
  },
  zoom_bundle_trial: {
    description: `Basic meeting measurement, summaries, and reporting for better meetings. Limited to ${Config.billing.monthlyRecordingQuota.zoom_bundle_trial} meetings per month.`,
    isMostPopular: false,
    featureDescription: "Key features include:",
    features: [
      {
        title: `${Config.billing.monthlyRecordingQuota.zoom_bundle_trial} meeting reports per month.`,
        description:
          "Summary, transcription with audience reactions, action items, key questions, and topics.",
      },
      {
        title: "Video conferencing",
        description:
          "Support for Zoom, Google Meet, Microsoft Teams, and Webex",
      },
      {
        title: "Basic reporting",
        description: "Access to recommendations and coaching",
      },
      {
        title: "Smart Scheduler",
        description: "Get the free Calendar add-on",
      },
    ],
  },
  pro: {
    description:
      "Robust team metrics, admin control, and advanced reporting with unlimited meetings.",
    isMostPopular: false,
    featureDescription: "Free features, plus:",
    alternativeFeatureDescription: "Zoom features, plus:",
    features: [
      {
        title: "Unlimited meeting reports",
      },
      {
        title: "Workspaces",
        description: "Admin control and team reporting",
      },
      {
        title: "Topic progression",
        description:
          "Advanced For You reporting with topic progression across meetings",
      },
      {
        title: "Integrations",
        description: "Zapier, Slack, and Webhooks",
      },
      {
        title: "Free upload credits",
        description: `${Config.billing.monthlyUploadCredits.pro} minutes per month`,
      },
    ],
  },
  enterprise: {
    description:
      "Video playback, prioritized support, and controls for scale with unlimited meetings.",
    isMostPopular: true,
    featureDescription: "Pro features, plus:",
    features: [
      {
        title: "Meeting playback",
        description:
          "Audio & video recordings with chapter annotations and more",
      },
      {
        title: "Highlight reels",
        description:
          "Rewatch a 2-minute recap of the meeting's pivotal moments",
      },
      {
        title: "Premium support",
        description: "Priority access to our support team",
      },
      {
        title: "Free upload credits",
        description: `${Config.billing.monthlyUploadCredits.enterprise} minutes per month`,
      },
    ],
  },
  enterprise_plus: {
    description:
      "For organizations that require additional security, control, and support. Requires a Workspace and 10+ licenses.",
    isMostPopular: false,
    featureDescription: "Enterprise features, plus:",
    features: [
      {
        title: "SSO & SAML",
        description: "Added security for authentication",
      },
      {
        title: "Domain capture",
      },
      {
        title: "Data retention policy",
        description: "Customized data deletion",
      },
      {
        title: "Unlimited meeting duration",
        description: "No caps on meeting lengths",
      },
      {
        title: "Free upload credits",
        description: `${Config.billing.monthlyUploadCredits.enterprise_plus} minutes per month`,
      },
    ],
  },
};
